import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Alert, Box, Button, Chip, CircularProgress, FormControl, InputLabel, Link,
    MenuItem, Paper, Select, Snackbar, Stack, TextField, Typography,
} from "@mui/material";
import FacebookIcon from "@mui/icons-material/Facebook";
import { config } from "../Const";
import { executeFetchAsync } from "../Util";

const TEMPLATES = [
    ["WRONG_ANSWER", "Wrong answer"],
    ["FULL_PLAYBACK", "Full playback"],
    ["BEAT_THE_CROWD", "Beat the crowd"],
    ["BEAT_THE_CLOCK", "Beat the clock"],
    ["THREE_FACTS", "Three facts"],
];

export default function FacebookReel({ seoTitle, type }) {
    const [template, setTemplate] = useState("WRONG_ANSWER");
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [connection, setConnection] = useState(null);
    const [connectionLoading, setConnectionLoading] = useState(true);
    const [loginPending, setLoginPending] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [publication, setPublication] = useState(null);
    const [message, setMessage] = useState(null);
    const popupTimer = useRef(null);

    const clearPopupTimer = useCallback(() => {
        if (popupTimer.current) window.clearInterval(popupTimer.current);
        popupTimer.current = null;
    }, []);

    useEffect(() => {
        setTitle("");
        setDescription("");
        setPublication(null);
    }, [seoTitle, template]);
    useEffect(() => () => clearPopupTimer(), [clearPopupTimer]);
    useEffect(() => () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
    }, [previewUrl]);

    const loadConnection = useCallback(async () => {
        setConnectionLoading(true);
        try {
            setConnection(await executeFetchAsync(
                `${config.SERVER_URL}/api/v1/admin/facebook/connection`, "get"));
        } catch (error) {
            setConnection(null);
            setMessage({ severity: "error", text: `Could not read Facebook connection: ${error.message}` });
        } finally {
            setConnectionLoading(false);
        }
    }, []);

    useEffect(() => { void loadConnection(); }, [loadConnection]);
    useEffect(() => {
        const receiveOAuth = (event) => {
            if (event.data?.type !== "quizmyday-facebook-oauth") return;
            clearPopupTimer();
            setLoginPending(false);
            setMessage(event.data.success
                ? { severity: "success", text: "Facebook Page connected" }
                : { severity: "error", text: "Facebook authorization failed" });
            if (event.data.success) void loadConnection();
        };
        window.addEventListener("message", receiveOAuth);
        return () => window.removeEventListener("message", receiveOAuth);
    }, [clearPopupTimer, loadConnection]);

    const connectFacebook = async () => {
        const popup = window.open("", "facebookLogin", "width=620,height=760,resizable=yes,scrollbars=yes");
        if (!popup) {
            setMessage({ severity: "warning", text: "Allow popups to connect Facebook" });
            return;
        }
        clearPopupTimer();
        setLoginPending(true);
        try {
            const response = await executeFetchAsync(
                `${config.SERVER_URL}/api/v1/admin/facebook/authorization-url`, "get");
            popup.location.replace(response.authorizationUrl);
            popupTimer.current = window.setInterval(() => {
                if (popup.closed) {
                    clearPopupTimer();
                    setLoginPending(false);
                    void loadConnection();
                }
            }, 600);
        } catch (error) {
            popup.close();
            setLoginPending(false);
            setMessage({ severity: "error", text: `Could not start Facebook login: ${error.message}` });
        }
    };

    const validationError = useMemo(() => {
        if (type !== "QUIZ") return "Facebook Reels can only be generated for quiz pages.";
        if (title.length > 255) return "Title cannot exceed 255 characters.";
        return null;
    }, [title, type]);

    const generatePreview = async () => {
        setGenerating(true);
        try {
            const blob = await executeFetchAsync(
                `${config.SERVER_URL}/api/v1/admin/teaser-video/generate`, "post",
                { "Content-Type": "application/json" },
                JSON.stringify({ quizSeoTitle: seoTitle, template, socialMedia: "FACEBOOK" }));
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            setPreviewUrl(URL.createObjectURL(blob));
            const query = new URLSearchParams({ quizSeoTitle: seoTitle, template, socialMedia: "FACEBOOK" });
            const generated = await executeFetchAsync(
                `${config.SERVER_URL}/api/v1/admin/teaser-video/publication?${query}`, "get");
            setPublication(generated);
            if (generated.title) setTitle(generated.title);
            if (generated.description) setDescription(generated.description);
            setMessage({ severity: "success", text: "Facebook Reel preview generated" });
        } catch (error) {
            setMessage({ severity: "error", text: `Preview generation failed: ${error.message}` });
        } finally {
            setGenerating(false);
        }
    };

    const publishReel = async () => {
        if (validationError) {
            setMessage({ severity: "warning", text: validationError });
            return;
        }
        setPublishing(true);
        try {
            const result = await executeFetchAsync(
                `${config.SERVER_URL}/api/v1/admin/teaser-video/publish`, "post",
                { "Content-Type": "application/json" },
                JSON.stringify({
                    quizSeoTitle: seoTitle, template, socialMedia: "FACEBOOK",
                    title: title.trim() || null, description: description.trim() || null,
                }));
            setPublication(result);
            setMessage({ severity: "success", text: "Facebook Reel published successfully" });
        } catch (error) {
            setMessage({ severity: "error", text: `Facebook publishing failed: ${error.message}` });
        } finally {
            setPublishing(false);
        }
    };

    if (type !== "QUIZ") return <Alert severity="info">Select a quiz to generate and publish a Facebook Reel.</Alert>;
    const published = publication?.status === "PUBLISHED";

    return <Stack spacing={3} sx={{ width: "100%" }}>
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}
                   alignItems={{ md: "center" }} justifyContent="space-between">
                <Box>
                    <Typography variant="subtitle1" fontWeight={700}>Facebook Page</Typography>
                    {connectionLoading ? <CircularProgress size={20} /> : connection?.connected ?
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Chip color="success" size="small" label="Connected" />
                            <Typography variant="body2">{connection.pageName}</Typography>
                            <Typography variant="caption" color="text.secondary">Page ID: {connection.pageId}</Typography>
                        </Stack> :
                        <Typography variant="body2" color="error">
                            {connection?.lastError || "Facebook is not connected."}
                        </Typography>}
                </Box>
                <Button variant={connection?.connected ? "outlined" : "contained"}
                        startIcon={loginPending ? <CircularProgress size={16} /> : <FacebookIcon />}
                        disabled={loginPending} onClick={connectFacebook}>
                    {connection?.connected ? "Reconnect Facebook" : "Connect Facebook"}
                </Button>
            </Stack>
        </Paper>

        <Alert severity="info">
            The generated description includes a clickable, tracked link to the full quiz.
        </Alert>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) minmax(320px, .8fr)" }, gap: 3 }}>
            <Stack spacing={2}>
                <FormControl fullWidth>
                    <InputLabel id="facebook-template-label">Teaser format</InputLabel>
                    <Select labelId="facebook-template-label" value={template} label="Teaser format"
                            onChange={(event) => setTemplate(event.target.value)}>
                        {TEMPLATES.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                    </Select>
                </FormControl>
                <TextField fullWidth label="Title (optional)" value={title}
                           onChange={(event) => setTitle(event.target.value)} error={title.length > 255}
                           helperText={`${title.length}/255 characters. Leave blank for the generated title.`}
                           inputProps={{ maxLength: 255 }} />
                <TextField fullWidth multiline minRows={7} label="Description (optional)"
                           value={description} onChange={(event) => setDescription(event.target.value)}
                           helperText="Leave blank to include the generated quiz link and campaign tracking." />
                {validationError && <Alert severity="warning">{validationError}</Alert>}
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <Button variant="outlined" disabled={generating || publishing} onClick={generatePreview}
                            startIcon={generating ? <CircularProgress size={16} /> : null}>Generate preview</Button>
                    <Button variant="contained" disabled={publishing || generating || !connection?.connected
                            || Boolean(validationError) || published} onClick={publishReel}
                            startIcon={publishing ? <CircularProgress size={16} /> : <FacebookIcon />}>
                        {published ? "Published to Facebook" : "Publish Facebook Reel"}
                    </Button>
                </Stack>
                {publication && <Alert severity={published ? "success" : publication.status === "FAILED" ? "error" : "info"}>
                    <Typography variant="body2">Status: {publication.status}</Typography>
                    {publication.facebookProcessingStatus && <Typography variant="caption" display="block">Facebook: {publication.facebookProcessingStatus}</Typography>}
                    {publication.failureMessage && <Typography variant="caption" display="block">{publication.failureMessage}</Typography>}
                    {publication.externalPublicationUrl && <Link href={publication.externalPublicationUrl}
                        target="_blank" rel="noreferrer" display="inline-block" sx={{ mt: 1 }}>Open the published Reel</Link>}
                </Alert>}
            </Stack>
            <Paper variant="outlined" sx={{ p: 2, minHeight: 320 }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>Video preview</Typography>
                {previewUrl ? <Box component="video" src={previewUrl} controls sx={{ width: "100%", maxHeight: 620, bgcolor: "black", borderRadius: 1 }} /> :
                    <Box sx={{ minHeight: 260, display: "grid", placeItems: "center", bgcolor: "grey.100", borderRadius: 1, p: 3 }}>
                        <Typography color="text.secondary" textAlign="center">Generate a preview before publishing.</Typography>
                    </Box>}
            </Paper>
        </Box>
        <Snackbar open={Boolean(message)} autoHideDuration={6000} onClose={() => setMessage(null)}>
            {message ? <Alert severity={message.severity} onClose={() => setMessage(null)}>{message.text}</Alert> : undefined}
        </Snackbar>
    </Stack>;
}
